import React from 'react';
import { WifiOff, ShieldOff, Timer, Bug } from 'lucide-react';
import { SPECTRUM_COLORS } from '@/utils/constants';

export { SPECTRUM_COLORS };

export function formatDateTime(iso: string, locale = 'pt-BR'): string {
  return new Date(iso).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export type InterferenceLevel =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'negligible';

export interface InterferenceInfo {
  level: InterferenceLevel;
  label: string;
  impact: string;
  action: string;
  color: string;
}

export function getInterferenceLevel(dBm: number): InterferenceLevel {
  if (dBm >= -55) return 'critical';
  if (dBm >= -70) return 'high';
  if (dBm >= -80) return 'medium';
  if (dBm >= -90) return 'low';
  return 'negligible';
}

export function getInterferenceColor(level: InterferenceLevel): string {
  switch (level) {
    case 'critical':
      return '#D32F2F';
    case 'high':
      return '#F97316';
    case 'medium':
      return '#FACC15';
    case 'low':
      return '#00CFFF';
    case 'negligible':
    default:
      return '#4CAF50';
  }
}

/** Get detailed interference impact and action based on neighboring RSSI with i18n support */
export function getInterferenceInfo(
  dBm: number,
  t?: (key: string) => string
): InterferenceInfo {
  const level = getInterferenceLevel(dBm);
  const color = getInterferenceColor(level);

  if (t) {
    return {
      level,
      color,
      label: t(`scanner.interference.levels.${level}.label`),
      impact: t(`scanner.interference.levels.${level}.impact`),
      action: t(`scanner.interference.levels.${level}.action`),
    };
  }

  // Fallback default Portuguese descriptions when t is not provided
  const fallbackDetails: Record<
    InterferenceLevel,
    { label: string; impact: string; action: string }
  > = {
    critical: {
      label: 'Crítica / Muito Alta',
      impact:
        'Disputa severa por tempo de antena (Co-canal) ou alto nível de ruído/perda de pacotes (Adjacente).',
      action:
        'Mude de canal imediatamente. O vizinho está muito próximo fisicamente.',
    },
    high: {
      label: 'Alta',
      impact:
        'Reduz perceptivelmente a velocidade da sua internet e aumenta a latência (ping) durante o uso simultâneo.',
      action: 'Evite este canal se houver alternativas mais limpas.',
    },
    medium: {
      label: 'Média / Moderada',
      impact:
        'Causa pequenos atrasos. É gerenciável se não houver muitas redes neste mesmo nível.',
      action: 'Aceitável caso os outros canais estejam piores.',
    },
    low: {
      label: 'Baixa',
      impact:
        'Quase imperceptível. O sinal vizinho chega muito fraco e é tratado como ruído de fundo tolerável.',
      action: 'Canal seguro para operar.',
    },
    negligible: {
      label: 'Inexistente / Desprezível',
      impact:
        'O sinal está no limite do "assoalho de ruído". Não causa impacto prático.',
      action: 'Canal ideal.',
    },
  };

  return {
    level,
    color,
    ...fallbackDetails[level],
  };
}

/** Convert dBm of neighboring network -> interference level color */
export function getSignalColor(dBm: number): string {
  return getInterferenceColor(getInterferenceLevel(dBm));
}

export const ERROR_ICONS: Record<DiagnosticsState, React.ReactNode> = {
  DeviceOffline: <WifiOff size={40} />,
  Unsupported: <ShieldOff size={40} />,
  Timeout: <Timer size={40} />,
  Error_Internal: <Bug size={40} />,
  Error_Other: <Bug size={40} />,
  Complete: null,
  None: null,
};

const FLOOR_DBM = -100;

/** Bandwidth string -> sigma in channel-number units */
export function bandwidthToSigma(bandwidth: string): number {
  const mhz = parseInt(bandwidth, 10) || 20;
  return mhz / 18;
}

/** Format bandwidth value ensuring 'MHz' suffix */
export function formatBandwidth(bandwidth: string | number | undefined): string {
  if (!bandwidth) return '20MHz';
  const str = String(bandwidth).trim();
  if (str.toUpperCase().endsWith('MHZ')) {
    return str;
  }
  const numeric = parseInt(str, 10);
  return isNaN(numeric) ? '20MHz' : `${numeric}MHz`;
}

/** Gaussian signal value at channel point `ch` for a given network */
export function gaussianSignal(
  ch: number,
  center: number,
  peakDbm: number,
  sigma: number
): number {
  if (isNaN(peakDbm) || !isFinite(peakDbm)) peakDbm = -70;
  if (isNaN(sigma) || sigma <= 0) sigma = 20 / 18;
  const val =
    FLOOR_DBM +
    (peakDbm - FLOOR_DBM) * Math.exp(-((ch - center) ** 2) / (2 * sigma ** 2));
  const rounded = Math.round(val * 10) / 10;
  return isNaN(rounded) ? FLOOR_DBM : rounded;
}

/** Generate fine-grained channel axis covering all networks */
export function buildSpectrumData(
  networks: WifiNetwork[]
): Record<string, number>[] {
  if (!networks || networks.length === 0) return [];
  const band = networks[0]?.band || '2.4GHz';
  const channels = networks
    .map((n) => n.channel)
    .filter((c) => !isNaN(c) && c > 0);
  if (channels.length === 0) return [];

  const minCh = Math.min(...channels);
  const maxCh = Math.max(...channels);

  // Define spectrum bounds and step per band
  const start = band === '2.4GHz' ? 0.5 : Math.max(30, minCh - 4);
  const end = band === '2.4GHz' ? 14.5 : Math.max(165, maxCh + 4);
  const step = band === '2.4GHz' ? 0.2 : 0.5;

  const points: number[] = [];
  for (let ch = start; ch <= end; ch = Math.round((ch + step) * 1000) / 1000) {
    points.push(ch);
  }

  return points.map((ch) => {
    const row: Record<string, number> = { channel: ch };
    networks.forEach((net, i) => {
      const sigma = bandwidthToSigma(net.bandwidth);
      row[`net_${i}`] = gaussianSignal(ch, net.channel, net.signal, sigma);
    });
    return row;
  });
}

/** Categorize channel quality by network congestion level */
export function getChannelQuality(networkCount: number): QualityLevel {
  if (networkCount === 0) return 'otimo';
  if (networkCount <= 2) return 'bom';
  if (networkCount <= 4) return 'regular';
  return 'congestionado';
}

const VALID_5GHZ_CHANNELS = [
  36, 40, 44, 48, 52, 56, 60, 64, 100, 104, 108, 112, 116, 120, 124, 128, 132,
  136, 140, 144, 149, 153, 157, 161, 165,
];

const VALID_24GHZ_CHANNELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

/** Calculate channel ranking for a given band based on detected networks */
export function calculateRanking(
  networks: WifiNetwork[],
  band: Band
): ChannelRanking[] {
  const baseChannels =
    band === '2.4GHz' ? VALID_24GHZ_CHANNELS : VALID_5GHZ_CHANNELS;

  const channelCounts: Record<number, number> = {};

  baseChannels.forEach((ch) => {
    channelCounts[ch] = 0;
  });

  networks.forEach((net) => {
    const ch = net.channel;
    channelCounts[ch] = (channelCounts[ch] || 0) + 1;
  });

  const sortedChannels = Object.keys(channelCounts)
    .map(Number)
    .sort((a, b) => a - b);

  return sortedChannels.map((channel) => {
    const count = channelCounts[channel] || 0;
    const isDfs = band === '5GHz' && channel >= 52 && channel <= 144;
    return {
      channel,
      networkCount: count,
      quality: getChannelQuality(count),
      dfs: isDfs,
    };
  });
}

/** Select the recommended channel with least interference, respecting hardware allowed channels if available */
export function getRecommendedChannel(
  band: Band,
  currentChannel: number,
  ranking: ChannelRanking[],
  allowedChannels?: number[]
): number {
  let validRanking = ranking;
  if (allowedChannels && allowedChannels.length > 0) {
    const filtered = ranking.filter((r) => allowedChannels.includes(r.channel));
    if (filtered.length > 0) {
      validRanking = filtered;
    }
  }

  if (!validRanking || validRanking.length === 0) {
    return currentChannel || (band === '2.4GHz' ? 1 : 36);
  }

  if (band === '2.4GHz') {
    // For 2.4GHz, non-overlapping channels are 1, 6, 11
    const nonOverlapping = [1, 6, 11];
    const candidateRanks = validRanking.filter((r) =>
      nonOverlapping.includes(r.channel)
    );

    if (candidateRanks.length > 0) {
      const minCount = Math.min(...candidateRanks.map((r) => r.networkCount));
      const currentRank = candidateRanks.find((r) => r.channel === currentChannel);
      if (currentRank && currentRank.networkCount === minCount) {
        return currentChannel;
      }
      const best = candidateRanks.find((r) => r.networkCount === minCount);
      if (best) return best.channel;
    }
  }

  // For 5GHz or fallback: Prefer non-DFS channels with minimum network count
  const nonDfsRanks = validRanking.filter((r) => !r.dfs);
  const ranksToEvaluate = nonDfsRanks.length > 0 ? nonDfsRanks : validRanking;

  const minCount = Math.min(...ranksToEvaluate.map((r) => r.networkCount));
  const currentRank = ranksToEvaluate.find((r) => r.channel === currentChannel);
  if (currentRank && currentRank.networkCount === minCount) {
    return currentChannel;
  }

  const best = ranksToEvaluate.find((r) => r.networkCount === minCount);
  return best ? best.channel : currentChannel || (band === '2.4GHz' ? 1 : 36);
}

/** Parse a single raw Wi-Fi network object */
export function parseWifiNetwork(
  raw: Record<string, unknown>,
  defaultBand?: Band,
  index = 0
): WifiNetwork {
  const channel =
    parseInt(String(raw?.channel ?? raw?.Channel ?? 1), 10) || 1;

  // Determine band from frequency or channel
  const rawFreq = String(raw?.frequency ?? raw?.Frequency ?? '');
  let band: Band = defaultBand ?? '2.4GHz';
  if (rawFreq.includes('5') || raw?.band === '5GHz' || channel > 14) {
    band = '5GHz';
  } else {
    band = '2.4GHz';
  }

  // Determine security
  let security = 'WPA2';
  if (raw?.security || raw?.Security) {
    security = String(raw.security ?? raw.Security);
  } else if (raw?.authentication && raw?.encryption) {
    const auth = String(raw.authentication).trim();
    const enc = String(raw.encryption).trim();
    security = auth === enc || enc === 'NONE' ? auth : `${auth} / ${enc}`;
  } else if (raw?.authentication) {
    security = String(raw.authentication);
  } else if (raw?.encryption) {
    security = String(raw.encryption);
  } else if (raw?.securityModeEnabled || raw?.SecurityModeEnabled) {
    security = String(raw.securityModeEnabled ?? raw.SecurityModeEnabled);
  }

  // Parse signal cleanly, ensuring numeric fallback (never NaN)
  const rawSignal =
    raw?.signal ??
    raw?.Signal ??
    raw?.signalStrength ??
    raw?.SignalStrength ??
    raw?.rssi ??
    raw?.RSSI;
  const parsedSignal = parseFloat(String(rawSignal ?? ''));
  let signal =
    !isNaN(parsedSignal) && isFinite(parsedSignal) ? parsedSignal : -70;

  // If equipment returns positive dBm magnitude (e.g. 75 instead of -75 dBm), convert to negative
  if (signal > 0) {
    signal = -signal;
  }

  return {
    id: (raw?.id as number) ?? index + 1,
    ssid: raw?.ssid
      ? String(raw.ssid)
      : raw?.SSID
      ? String(raw.SSID)
      : raw?.name
      ? String(raw.name)
      : '',
    bssid: String(
      raw?.bssid ??
        raw?.BSSID ??
        raw?.macAddress ??
        raw?.mac ??
        raw?.BSSIDReference ??
        '00:00:00:00:00:00'
    ),
    channel,
    bandwidth: formatBandwidth(
      (raw?.bandWidth ??
        raw?.bandwidth ??
        raw?.Bandwidth ??
        raw?.operatingChannelBandwidth ??
        raw?.OperatingChannelBandwidth) as string
    ),
    signal,
    security,
    standard: String(
      raw?.standard ??
        raw?.Standard ??
        raw?.operatingStandards ??
        raw?.OperatingStandards ??
        raw?.supportedStandards ??
        '802.11n'
    ),
    band,
  };
}

/** Extract networks from raw TR-069 result array or object structures */
function extractNetworks(resultData: unknown): {
  networks24: WifiNetwork[];
  networks50: WifiNetwork[];
} {
  const networks24: WifiNetwork[] = [];
  const networks50: WifiNetwork[] = [];

  const addNetwork = (net: WifiNetwork) => {
    if (net.band === '5GHz' || net.channel > 14) {
      networks50.push({ ...net, band: '5GHz' });
    } else {
      networks24.push({ ...net, band: '2.4GHz' });
    }
  };

  if (!resultData) {
    return { networks24, networks50 };
  }

  const record = resultData as Record<string, unknown>;

  // Format 1: Explicit 2.4GHz / 5GHz bands
  if (record['2.4GHz'] || record['5GHz']) {
    const list24 =
      (record['2.4GHz'] as Record<string, unknown>)?.networks ||
      (Array.isArray(record['2.4GHz']) ? record['2.4GHz'] : []);
    const list50 =
      (record['5GHz'] as Record<string, unknown>)?.networks ||
      (Array.isArray(record['5GHz']) ? record['5GHz'] : []);

    if (Array.isArray(list24)) {
      list24.forEach((item, idx) =>
        networks24.push(
          parseWifiNetwork(item as Record<string, unknown>, '2.4GHz', idx)
        )
      );
    }
    if (Array.isArray(list50)) {
      list50.forEach((item, idx) =>
        networks50.push(
          parseWifiNetwork(
            item as Record<string, unknown>,
            '5GHz',
            idx + 100
          )
        )
      );
    }
    if (networks24.length > 0 || networks50.length > 0) {
      return { networks24, networks50 };
    }
  }

  // Format 2: Object with radios (e.g. radio1, radio2 or Radio.1, Radio.2)
  if (
    typeof resultData === 'object' &&
    !Array.isArray(resultData) &&
    !Array.isArray(record.result) &&
    !Array.isArray(record.networks)
  ) {
    const keys = Object.keys(record);
    let matchedRadio = false;

    keys.forEach((key) => {
      const lower = key.toLowerCase();
      const val = record[key];
      if (Array.isArray(val)) {
        if (
          lower.includes('radio.1') ||
          lower.includes('radio1') ||
          lower.includes('2.4') ||
          lower.includes('2g')
        ) {
          val.forEach((item, idx) =>
            networks24.push(
              parseWifiNetwork(item as Record<string, unknown>, '2.4GHz', idx)
            )
          );
          matchedRadio = true;
        } else if (
          lower.includes('radio.2') ||
          lower.includes('radio2') ||
          lower.includes('5') ||
          lower.includes('5g')
        ) {
          val.forEach((item, idx) =>
            networks50.push(
              parseWifiNetwork(
                item as Record<string, unknown>,
                '5GHz',
                idx + 100
              )
            )
          );
          matchedRadio = true;
        }
      }
    });

    if (matchedRadio) {
      return { networks24, networks50 };
    }
  }

  // Format 3: Flat array in resultData, resultData.result, resultData.networks, etc.
  const rawList = Array.isArray(resultData)
    ? (resultData as unknown[])
    : Array.isArray(record?.result)
    ? (record.result as unknown[])
    : Array.isArray(record?.networks)
    ? (record.networks as unknown[])
    : Array.isArray(record?.neighboringWiFiDiagnostics)
    ? (record.neighboringWiFiDiagnostics as unknown[])
    : [];

  rawList.forEach((item: unknown, idx: number) => {
    addNetwork(
      parseWifiNetwork((item as Record<string, unknown>) || {}, undefined, idx)
    );
  });

  return { networks24, networks50 };
}

/** Parse and normalize raw diagnostic API response into ScanResult */
export function parseScanResult(
  data: unknown,
  equipment?: EquipmentType | null
): ScanResult {
  if (!data) {
    return {
      diagnosticsState: 'None',
      timestamp: new Date().toISOString(),
      '2.4GHz': {
        currentChannel: 1,
        recommendedChannel: 1,
        ranking: calculateRanking([], '2.4GHz'),
        networks: [],
      },
      '5GHz': {
        currentChannel: 36,
        recommendedChannel: 36,
        ranking: calculateRanking([], '5GHz'),
        networks: [],
      },
    };
  }

  const payload = data as Record<string, unknown>;

  // Determine current channels from equipment
  const radio24 = equipment?.radios?.find(
    (r) => r?.frequency?.value === '2.4GHz' || r?.frequency?.value === '2.4'
  );
  const radio50 = equipment?.radios?.find(
    (r) => r?.frequency?.value === '5GHz' || r?.frequency?.value === '5'
  );
  const wifi24 = equipment?.wifis?.find(
    (w) => w?.frequency?.value === '2.4GHz' || w?.frequency?.value === '2.4'
  );
  const wifi50 = equipment?.wifis?.find(
    (w) => w?.frequency?.value === '5GHz' || w?.frequency?.value === '5'
  );

  const fallbackCurrent24 =
    Number(
      radio24?.channel?.value ||
        radio24?.channelsInUse?.value ||
        wifi24?.channel?.value
    ) || 1;

  const fallbackCurrent50 =
    Number(
      radio50?.channel?.value ||
        radio50?.channelsInUse?.value ||
        wifi50?.channel?.value
    ) || 36;

  // Extract networks first
  const resultPayload =
    payload.result !== undefined && payload.result !== null
      ? payload.result
      : payload;
  const { networks24, networks50 } = extractNetworks(resultPayload);

  // Determine diagnosticsState
  let diagnosticsState: DiagnosticsState = 'Complete';
  if (payload.diagnosticsState) {
    diagnosticsState = payload.diagnosticsState as DiagnosticsState;
  } else if (networks24.length > 0 || networks50.length > 0) {
    // If networks were returned, the diagnostic executed successfully
    diagnosticsState = 'Complete';
  } else if (Array.isArray(payload.status)) {
    const statusList = payload.status as Array<Record<string, unknown>>;
    const errorStatus = statusList.find(
      (s: Record<string, unknown>) =>
        s.value &&
        s.value !== 'Complete' &&
        s.value !== 'None' &&
        s.value !== 'Requested'
    );
    if (errorStatus) {
      diagnosticsState =
        errorStatus.value === 'Error_Internal'
          ? 'Error_Internal'
          : errorStatus.value === 'DeviceOffline'
          ? 'DeviceOffline'
          : errorStatus.value === 'Timeout'
          ? 'Timeout'
          : errorStatus.value === 'Unsupported'
          ? 'Unsupported'
          : 'Error_Other';
    } else {
      diagnosticsState = 'Complete';
    }
  }

  // Extract supported channel options from equipment.radios
  const options24 = Array.isArray(radio24?.channel?.options)
    ? (radio24.channel.options as Array<string | number>)
        .map(Number)
        .filter((n: number) => !isNaN(n))
    : undefined;
  const options50 = Array.isArray(radio50?.channel?.options)
    ? (radio50.channel.options as Array<string | number>)
        .map(Number)
        .filter((n: number) => !isNaN(n))
    : undefined;

  // 2.4GHz band result
  const band24Obj = payload?.['2.4GHz'] as
    | Record<string, unknown>
    | undefined;
  const currentChannel24 =
    Number(band24Obj?.currentChannel) || fallbackCurrent24;
  const ranking24 =
    band24Obj?.ranking && Array.isArray(band24Obj.ranking)
      ? (band24Obj.ranking as ChannelRanking[])
      : calculateRanking(networks24, '2.4GHz');
  const recommendedChannel24 =
    Number(band24Obj?.recommendedChannel) ||
    getRecommendedChannel('2.4GHz', currentChannel24, ranking24, options24);

  // 5GHz band result
  const band50Obj = payload?.['5GHz'] as
    | Record<string, unknown>
    | undefined;
  const currentChannel50 =
    Number(band50Obj?.currentChannel) || fallbackCurrent50;
  const ranking50 =
    band50Obj?.ranking && Array.isArray(band50Obj.ranking)
      ? (band50Obj.ranking as ChannelRanking[])
      : calculateRanking(networks50, '5GHz');
  const recommendedChannel50 =
    Number(band50Obj?.recommendedChannel) ||
    getRecommendedChannel('5GHz', currentChannel50, ranking50, options50);

  return {
    diagnosticsState,
    timestamp: (payload.timestamp as string) || new Date().toISOString(),
    '2.4GHz': {
      currentChannel: currentChannel24,
      recommendedChannel: recommendedChannel24,
      ranking: ranking24,
      networks: networks24,
    },
    '5GHz': {
      currentChannel: currentChannel50,
      recommendedChannel: recommendedChannel50,
      ranking: ranking50,
      networks: networks50,
    },
  };
}
